<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { AxiosAPI } from '@/axios';
import { useMainStore } from '../../../stores/index';
import moment from 'moment';
import Breadcrumb from '../patchName.vue';
import DocumentTableLayout from './DocumentTableLayout.vue';

const Store = useMainStore();
Store.Token();
const router = useRouter();

const documents = ref([]);
const types = ref([]);
const loading = ref(true);
const removeDraftSuccess = ref(false);

const showDeleteModal = ref(false);
const draftToDelete = ref(null);

const getDocTypeName = (typeId) => {
  const found = types.value.find((t) => t.id === typeId);
  return found ? found.title : 'Rascunho';
};

const formatDate = (value) => {
  if (!value) return '-';
  moment.locale('pt-br');
  return moment(value).format('DD/MM/YYYY');
};

const getTypes = async () => {
  const typesAPI = await Store.GetTypes();
  types.value = typesAPI || [];
  getDrafts();
};

const getDrafts = () => {
  loading.value = true;
  AxiosAPI.get('api/drafts', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + Store.accessToken
    }
  })
    .then((response) => {
      const docs = [];
      if (response.data && response.data.drafts) {
        response.data.drafts.forEach((element) => {
          try {
            element.body = typeof element.content === 'string' ? JSON.parse(element.content) : element.content;
          } catch (e) {
            element.body = [{ body: '' }];
          }
          
          docs.push({
            ...element,
            typeTitle: getDocTypeName(element.document_type_id),
            formattedDate: formatDate(element.updated_at || element.created_at)
          });
        });
      }
      documents.value = docs;
      loading.value = false;
    })
    .catch(() => {
      loading.value = false;
    });
};

const confirmRemoveDraft = (draft) => {
  draftToDelete.value = draft;
  showDeleteModal.value = true;
};

const executeRemoveDraft = () => {
  if (!draftToDelete.value) return;
  
  loading.value = true;
  showDeleteModal.value = false;

  AxiosAPI.delete('api/drafts/' + draftToDelete.value.id, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + Store.accessToken
    }
  })
    .then(() => {
      removeDraftSuccess.value = true;
      draftToDelete.value = null;
      getDrafts();
    })
    .catch(() => {
      loading.value = false;
    });
};

const editDraft = (draft) => {
  Store.is_new_document = false;
  Store.draftID = draft.id;
  Store.progress = 2;
  Store.documentData = {
    to_users: [],
    to_departments: [],
    typeDoc: draft.document_type_id,
    attachments: [],
    signerAttach: null,
    subject: draft.subject,
    body: draft.body && draft.body[0] ? draft.body[0].body : '',
    status_sign: 0,
    user_id: draft.user_id
  };
  router.push({ name: 'Criar de documento' });
};

onMounted(() => {
  getTypes();
});
</script>

<template>
  <div class="py-4 container-fluid" id="Rascunhos">
    <Breadcrumb />
    
    <h4 class="page-title mb-4">Rascunhos</h4>

    <div
      v-if="removeDraftSuccess"
      class="alert-success-custom d-flex justify-content-between align-items-center mb-4 px-3"
    >
      <div class="d-flex align-items-center">
        <font-awesome-icon :icon="['fas', 'check-circle']" class="me-2 icon-success" />
        <span class="font-docs text-success-dark"><b>Rascunho removido com sucesso.</b></span>
      </div>
      <button type="button" class="btn-close-alert" @click="removeDraftSuccess = false">
        <font-awesome-icon :icon="['fas', 'xmark']" />
      </button>
    </div>

    <DocumentTableLayout
      title="Rascunhos"
      :items="documents"
      :loading="loading"
      :searchFilterKeys="['subject', 'typeTitle']"
    >
      <template #actions="{ item }">
        <div class="d-inline-flex gap-2">
          <button
            type="button"
            class="btn-action btn-orange"
            title="Editar Rascunho"
            @click="editDraft(item)"
          >
            <font-awesome-icon :icon="['fas', 'pen-to-square']" />
          </button>
          
          <button
            type="button"
            class="btn-action btn-orange"
            title="Excluir Rascunho"
            @click="confirmRemoveDraft(item)"
          >
            <font-awesome-icon :icon="['fas', 'trash']" />
          </button>

          <button
            type="button"
            class="btn-action btn-next"
            title="Acessar"
            @click="editDraft(item)"
          >
            <font-awesome-icon :icon="['fas', 'chevron-right']" />
          </button>
        </div>
      </template>
    </DocumentTableLayout>

    <div
      v-if="showDeleteModal"
      class="modal-backdrop-custom d-flex align-items-center justify-content-center"
    >
      <div class="modal-card p-4">
        <div class="text-center mb-3">
          <div class="icon-warning-circle mx-auto mb-2 d-flex align-items-center justify-content-center">
            <font-awesome-icon :icon="['fas', 'triangle-exclamation']" />
          </div>
          <h5 class="fw-bold text-dark font-docs mb-1">Excluir rascunho?</h5>
          <p class="text-muted font-docs fs-7 mb-0">
            Esta ação é permanente e <b>não poderá ser desfeita</b>. O rascunho selecionado será removido do sistema.
          </p>
        </div>

        <div class="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-light-custom flex-grow-1 font-docs"
            @click="showDeleteModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-danger-custom flex-grow-1 font-docs"
            @click="executeRemoveDraft"
          >
            Sim, excluir
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-title {
  color: #212529;
  font-weight: 600;
  font-size: 22px;
}

.btn-action {
  background: transparent;
  border: none;
  padding: 6px 10px;
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: 14px;
}

.btn-orange {
  color: #f1860b !important;
}

.btn-orange:hover {
  background-color: rgba(241, 134, 11, 0.12);
}

.btn-next {
  color: #a0aec0;
}

.btn-next:hover {
  color: #212529;
  background-color: rgba(0, 0, 0, 0.05);
}

.alert-success-custom {
  background-color: #badbcc;
  height: 50px;
  border-radius: 8px;
}

.icon-success {
  color: #0f5132;
  font-size: 16px;
}

.text-success-dark {
  color: #0f5132;
}

.btn-close-alert {
  background: transparent;
  border: none;
  color: #7d787f;
  cursor: pointer;
}

.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  padding: 16px;
}

.modal-card {
  background-color: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: fadeInModal 0.2s ease-out;
}

.icon-warning-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #fff3cd;
  color: #f1860b;
  font-size: 22px;
}

.btn-light-custom {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  color: #495057;
  font-weight: 500;
  border-radius: 6px;
  padding: 8px 16px;
}

.btn-light-custom:hover {
  background-color: #e2e8f0;
}

.btn-danger-custom {
  background-color: #dc3545;
  border: none;
  color: #ffffff;
  font-weight: 500;
  border-radius: 6px;
  padding: 8px 16px;
}

.btn-danger-custom:hover {
  background-color: #bb2d3b;
}

@keyframes fadeInModal {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>